import { useCallback, useEffect, useRef, useState } from "react";

import apiClient from "../../../services/apiClient";
import "./PaperHomeworkGradingPage.css";

// 문제지 관리 > 출제 문제지 목록 > '지필과제' 필터(2026-10-05). 알림장으로 낸 지필과제를 같은 날·같은 반·같은 제목끼리 한 줄로 묶어
// 제출·채점 현황을 보고, 한 줄을 열면 반 학생 전부의 제출 사진과 점수 칸이 한 화면에 나온다.
// 점수 칸에서 Enter 를 누르면 저장하고 다음 학생으로 넘어간다. 서버: api/paper_assignment/grading_views.py
// 점수 저장은 알림장 화면과 같은 grade-submission 을 쓴다(채점 완료 알림톡도 그쪽에서 나간다).

const BASE = "/api/v1/paper-assignment";
const FILTER_LIST = [
  { key: "all", label: "전체" },
  { key: "waiting", label: "채점 대기" },
  { key: "missing", label: "미제출" },
];

const formatDate = (value) => {
  if (!value) return "";
  const date = new Date(value);
  return `${date.getMonth() + 1}.${date.getDate()}`;
};

const isImage = (file) => /\.(png|jpe?g|gif|webp|heic)$/i.test(file.name || file.url || "");

const GroupList = ({ keyword, onOpen }) => {
  const [data, setData] = useState(null);
  const [page, setPage] = useState(1);

  useEffect(() => {
    setPage(1);
  }, [keyword]);

  useEffect(() => {
    apiClient
      .get(`${BASE}/homework-groups/?page=${page}&keyword=${encodeURIComponent(keyword || "")}`, { withAuth: true })
      .then((response) => setData(response.data))
      .catch(() => setData({ group_list: [], page: 1, page_count: 1, waiting_count: 0 }));
  }, [page, keyword]);

  if (!data) return null;
  if (data.group_list.length === 0) return <p className="paper-grading__empty">최근 낸 지필과제가 없어요.</p>;

  return (
    <>
      <table className="paper-grading__table">
        <thead>
          <tr>
            <th>날짜</th>
            <th>반</th>
            <th>과제</th>
            <th>마감</th>
            <th>제출</th>
            <th>채점</th>
          </tr>
        </thead>
        <tbody>
          {data.group_list.map((group) => {
            const waiting = group.submitted_count - group.graded_count;
            return (
              <tr key={group.key} onClick={() => onOpen(group.key)}>
                <td>{formatDate(group.date)}</td>
                <td>{group.class_name}</td>
                <td className="paper-grading__title">
                  {group.title}
                  {waiting > 0 && <span className="paper-grading__badge">채점 대기 {waiting}</span>}
                </td>
                <td>{formatDate(group.end_date) || "-"}</td>
                <td>
                  {group.submitted_count}/{group.student_count}
                </td>
                <td>
                  {group.graded_count}/{group.submitted_count}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      {data.page_count > 1 && (
        <div className="paper-grading__pager">
          <button type="button" disabled={page <= 1} onClick={() => setPage(page - 1)}>
            이전
          </button>
          <span>
            {page} / {data.page_count}
          </span>
          <button type="button" disabled={page >= data.page_count} onClick={() => setPage(page + 1)}>
            다음
          </button>
        </div>
      )}
    </>
  );
};

const GroupDetail = ({ groupKey, onBack }) => {
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState("all");
  const [scoreMap, setScoreMap] = useState({});
  const [feedbackMap, setFeedbackMap] = useState({});
  const [savingId, setSavingId] = useState(null);
  const inputRefs = useRef({});

  const load = useCallback(
    () =>
      apiClient
        .get(`${BASE}/homework-groups/detail/?key=${encodeURIComponent(groupKey)}`, { withAuth: true })
        .then((response) => {
          setData(response.data);
          const next = {};
          response.data.row_list.forEach((row) => {
            if (row.submission) next[row.submission.id] = row.submission.score ?? "";
          });
          setScoreMap(next);
        }),
    [groupKey]
  );

  useEffect(() => {
    load();
  }, [load]);

  if (!data) return null;

  const rowList = data.row_list.filter((row) => {
    if (filter === "waiting") return row.submission && row.submission.score === null;
    if (filter === "missing") return !row.submission;
    return true;
  });
  const gradableIdList = rowList.filter((row) => row.submission).map((row) => row.submission.id);

  const save = async (submissionId) => {
    const score = Number(scoreMap[submissionId]);
    if (scoreMap[submissionId] === "" || Number.isNaN(score) || score < 0 || score > 100) {
      alert("점수는 0~100 사이 숫자로 넣어 주세요.");
      return false;
    }
    const formData = new FormData();
    formData.append("submission_id", submissionId);
    formData.append("score", String(score));
    if (feedbackMap[submissionId]) formData.append("feedback_file", feedbackMap[submissionId]);
    setSavingId(submissionId);
    try {
      await apiClient.post(`${BASE}/grade-submission/`, formData, {
        withAuth: true,
        headers: { "Content-Type": "multipart/form-data" },
      });
      setFeedbackMap((previous) => ({ ...previous, [submissionId]: null }));
      await load();
      return true;
    } catch (error) {
      alert(error?.response?.data?.error || "저장하지 못했어요.");
      return false;
    } finally {
      setSavingId(null);
    }
  };

  const saveAndNext = async (submissionId) => {
    if (!(await save(submissionId))) return;
    const index = gradableIdList.indexOf(submissionId);
    const nextId = gradableIdList[index + 1];
    if (nextId && inputRefs.current[nextId]) inputRefs.current[nextId].focus();
  };

  const submittedCount = data.row_list.filter((row) => row.submission).length;
  const gradedCount = data.row_list.filter((row) => row.submission && row.submission.score !== null).length;

  return (
    <div>
      <button type="button" className="paper-grading__back" onClick={onBack}>
        ‹ 목록
      </button>
      <div className="paper-grading__head">
        <div>
          <div className="paper-grading__head-title">{data.title}</div>
          <div className="paper-grading__head-sub">
            {data.class_name} · {formatDate(data.date)}
            {data.end_date && ` · 마감 ${formatDate(data.end_date)}`}
            {data.homework_file_url && (
              <a href={data.homework_file_url} target="_blank" rel="noreferrer">
                📎 {data.homework_file_name || "과제 파일"}
              </a>
            )}
          </div>
        </div>
        <div className="paper-grading__head-count">
          제출 {submittedCount}/{data.row_list.length} · 채점 {gradedCount}/{submittedCount}
        </div>
      </div>
      <div className="paper-grading__filters">
        {FILTER_LIST.map((one) => (
          <button
            key={one.key}
            type="button"
            aria-pressed={filter === one.key}
            onClick={() => setFilter(one.key)}
          >
            {one.label}
          </button>
        ))}
      </div>
      <div className="paper-grading__rows">
        {rowList.map((row) => {
          const submission = row.submission;
          return (
            <div key={row.homework_id} className="paper-grading__row">
              <div className="paper-grading__student">
                <b>{row.student_name}</b>
                {!submission && <span className="paper-grading__state paper-grading__state--missing">미제출</span>}
                {submission && submission.score === null && (
                  <span className="paper-grading__state paper-grading__state--waiting">채점 대기</span>
                )}
                {submission && submission.score !== null && (
                  <span className="paper-grading__state paper-grading__state--done">{submission.score}점</span>
                )}
                {submission && <small>{formatDate(submission.submitted_at)} 제출</small>}
              </div>
              <div className="paper-grading__files">
                {submission?.file_list.map((file, index) =>
                  isImage(file) ? (
                    <a key={file.url || index} href={file.url} target="_blank" rel="noreferrer">
                      <img src={file.url} alt={`${row.student_name} 제출 ${index + 1}`} loading="lazy" />
                    </a>
                  ) : (
                    <a key={file.url || index} className="paper-grading__file" href={file.url} target="_blank" rel="noreferrer">
                      📎 {file.name || `파일 ${index + 1}`}
                    </a>
                  )
                )}
              </div>
              {submission && (
                <div className="paper-grading__grade">
                  <input
                    ref={(element) => {
                      inputRefs.current[submission.id] = element;
                    }}
                    type="number"
                    min={0}
                    max={100}
                    inputMode="numeric"
                    placeholder="점수"
                    value={scoreMap[submission.id] ?? ""}
                    onChange={(event) => setScoreMap((previous) => ({ ...previous, [submission.id]: event.target.value }))}
                    onKeyDown={(event) => {
                      if (event.key === "Enter") saveAndNext(submission.id);
                    }}
                  />
                  {/* 하·중·상을 누르면 40·70·100 을 칸에 넣는다(저장은 따로). 손으로 고쳐 넣어도 된다 */}
                  <div className="paper-grading__quick">
                    {QUICK_SCORE_LIST.map((one) => (
                      <button
                        key={one.label}
                        type="button"
                        className={String(scoreMap[submission.id]) === String(one.score) ? "is-on" : ""}
                        onClick={() => {
                          setScoreMap((previous) => ({ ...previous, [submission.id]: one.score }));
                          inputRefs.current[submission.id]?.focus();
                        }}
                      >
                        {one.label} {one.score}
                      </button>
                    ))}
                  </div>
                  <label className="paper-grading__feedback">
                    {feedbackMap[submission.id]?.name ||
                      (submission.feedback_file_name ? `첨삭: ${submission.feedback_file_name}` : "첨삭 파일")}
                    <input
                      type="file"
                      hidden
                      onChange={(event) =>
                        setFeedbackMap((previous) => ({ ...previous, [submission.id]: event.target.files[0] || null }))
                      }
                    />
                  </label>
                  <button type="button" disabled={savingId === submission.id} onClick={() => saveAndNext(submission.id)}>
                    {savingId === submission.id ? "저장 중" : "저장"}
                  </button>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

// 지필 점수 빠른 입력(2026-10-06 대표): 하 40 · 중 70 · 상 100
const QUICK_SCORE_LIST = [
  { label: "하", score: 40 },
  { label: "중", score: 70 },
  { label: "상", score: 100 },
];

const PaperHomeworkGradingPage = ({ keyword = "" }) => {
  const [openKey, setOpenKey] = useState("");
  return (
    <div className="paper-grading">
      {openKey ? (
        <GroupDetail groupKey={openKey} onBack={() => setOpenKey("")} />
      ) : (
        <GroupList keyword={keyword} onOpen={setOpenKey} />
      )}
    </div>
  );
};

export default PaperHomeworkGradingPage;
